import { useCallback, useEffect, useRef, useState } from "react";

/** Keep in sync with CSS animation duration in Dialog.scss / marketing.scss */
export const MODAL_EXIT_MS = 220;

export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Keeps a modal mounted through a short exit fade, then calls onClose
 * (when close was user-initiated) or just unmounts (when `open` became false).
 */
export function useModalExit({ open = true, onClose }) {
  const [rendered, setRendered] = useState(!!open);
  const [exiting, setExiting] = useState(false);
  const invokeOnCloseRef = useRef(false);
  const closeArgsRef = useRef([]);
  const renderedRef = useRef(!!open);
  const exitingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  renderedRef.current = rendered;
  exitingRef.current = exiting;

  useEffect(() => {
    if (open) {
      setRendered(true);
      setExiting(false);
      invokeOnCloseRef.current = false;
      closeArgsRef.current = [];
      return;
    }
    if (renderedRef.current) {
      invokeOnCloseRef.current = false;
      setExiting(true);
    }
  }, [open]);

  useEffect(() => {
    if (!exiting) return undefined;
    const ms = prefersReducedMotion() ? 0 : MODAL_EXIT_MS;
    const t = window.setTimeout(() => {
      const shouldInvoke = invokeOnCloseRef.current;
      const args = closeArgsRef.current;
      invokeOnCloseRef.current = false;
      closeArgsRef.current = [];
      setExiting(false);
      setRendered(false);
      if (shouldInvoke) onCloseRef.current?.(...args);
    }, ms);
    return () => window.clearTimeout(t);
  }, [exiting]);

  const requestClose = useCallback((...args) => {
    if (exitingRef.current || !renderedRef.current) return;
    invokeOnCloseRef.current = true;
    closeArgsRef.current = args;
    setExiting(true);
  }, []);

  return { rendered, exiting, requestClose };
}
